"use client";

import { Form, Formik } from "formik";
import { useState } from "react";

import {
  AUTH_FORM_CREATE_ACCOUNT_TEXT,
  AUTH_FORM_LOG_IN_TEXT,
  FRONT_END_API,
} from "@shared/constants";

import { logError } from "@shared/functions/log";

import postUser from "@server/front-end-api/postUser";
import postUserSignIn from "@server/front-end-api/postUserSignIn";

import Field from "@client/components/auth-form/components/field";
import PasswordInput from "@client/components/auth-form/components/password-input";
import PasswordStrength from "@client/components/auth-form/components/password-strength";
import ServerError from "@client/components/auth-form/components/server-error";
import SystemAlert from "@client/components/system-alert";
import resolveAuthFormServerErrorMessage from "@client/components/auth-form/functions/resolveAuthFormServerErrorMessage";
import Input from "@client/components/formik/input";
import { postEvent } from "@client/functions/client-actions";
import { useRouter } from "@client/functions/custom-hooks/useRouter";

import { FormButton } from "@ui/js/components/buttons/form-button";
import { SimpleButton } from "@ui/js/components/buttons/simple-button";

import type TAuthForm from "@packages/types/auth-form";
import {
  defaultFieldValues,
  validationSchema,
} from "@packages/types/yup/auth-form";

const INPUT_ID_AND_LABEL_HTMLFOR_EMAIL = "email";
const INPUT_ID_AND_LABEL_HTMLFOR_PASSWORD = "password";

const variantContent = {
  "create-account": {
    buttonText: AUTH_FORM_CREATE_ACCOUNT_TEXT,
    crossLinkText: AUTH_FORM_LOG_IN_TEXT,
    title: AUTH_FORM_CREATE_ACCOUNT_TEXT,
  },
  "log-in": {
    buttonText: AUTH_FORM_LOG_IN_TEXT,
    crossLinkText: AUTH_FORM_CREATE_ACCOUNT_TEXT,
    title: AUTH_FORM_LOG_IN_TEXT,
  },
};

interface Props {
  createAccountHref?: string;
  logInHref?: string;
  onSubmit?: TAuthForm.OnSubmit;
  onSuccess?: TAuthForm.OnSuccess;
  systemAlertMessage?: string;
  variant: TAuthForm.Variant;
}

const submitByVariant = {
  "create-account": postUser,
  "log-in": postUserSignIn,
};

export default function AuthForm({
  createAccountHref,
  logInHref,
  onSubmit,
  onSuccess,
  systemAlertMessage,
  variant,
}: Props) {
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(
    null,
  );
  const router = useRouter();

  const isCreateAccount = variant === "create-account";
  const { buttonText, crossLinkText, title } = variantContent[variant];
  const crossLinkHref = isCreateAccount ? logInHref : createAccountHref;
  const submit = onSubmit ?? submitByVariant[variant];

  const handleSubmit = async (values: TAuthForm.Values) => {
    setServerErrorMessage(null);

    try {
      const result = await submit(values);

      if (result?.error) {
        setServerErrorMessage(
          resolveAuthFormServerErrorMessage(result.error),
        );

        postEvent("error", {
          error: {
            error_message: `An error occurred while attempting to submit the ${title} form.`,
            error_object: "Auth Form",
            error_type: "Front-End API",
            path: window.location.href,
          },
        });

        logError({
          message: `An error occurred while attempting to submit the ${title} form.`,
          error: result.error,
          event: {
            destination: FRONT_END_API,
            name: "AuthForm",
            meta: { variant },
          },
        });

        return;
      }

      onSuccess?.();
    } catch (error) {
      setServerErrorMessage(resolveAuthFormServerErrorMessage(error));

      postEvent("error", {
        error: {
          error_message: `An error occurred while attempting to submit the ${title} form.`,
          error_object: "Auth Form",
          error_type: "Front-End API",
          path: window.location.href,
        },
      });

      logError({
        message: `An error occurred while attempting to submit the ${title} form.`,
        error,
        event: {
          destination: FRONT_END_API,
          name: "AuthForm",
          meta: { variant },
        },
      });
    }
  };

  return (
    <div className="card w-full">
      <Formik
        initialValues={defaultFieldValues[variant]}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema[variant]}
      >
        <Form
          className="form"
          data-spec-auth-form
          method="POST"
          name={variant}
          noValidate={true}
        >
          <h1 className="font-semibold leading-8 pb-2 text-[32px] text-center text-neutral-900">
            {title}
          </h1>
          {systemAlertMessage && <SystemAlert message={systemAlertMessage} />}
          {serverErrorMessage && <ServerError message={serverErrorMessage} />}
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
            label="Email"
            name={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
          >
            <Input
              autoComplete="email"
              id={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
              name={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
              type="email"
            />
          </Field>
          <Field
            helper={
              isCreateAccount ? (
                <PasswordStrength name={INPUT_ID_AND_LABEL_HTMLFOR_PASSWORD} />
              ) : undefined
            }
            id={INPUT_ID_AND_LABEL_HTMLFOR_PASSWORD}
            label="Password"
            name={INPUT_ID_AND_LABEL_HTMLFOR_PASSWORD}
          >
            <PasswordInput
              autoComplete={
                isCreateAccount ? "new-password" : "current-password"
              }
              id={INPUT_ID_AND_LABEL_HTMLFOR_PASSWORD}
              name={INPUT_ID_AND_LABEL_HTMLFOR_PASSWORD}
            />
          </Field>
          <div className="grid gap-2 pt-2">
            <FormButton
              className="h-14 hover:shadow-lg normal-case"
              text={buttonText}
            />
            {crossLinkHref && (
              <SimpleButton
                className="!w-full h-auto justify-center !text-center hover:shadow-none min-w-48 normal-case py-2 self-stretch shadow-none"
                color="action"
                data-spec="auth-form-cross-link"
                onClick={() => {
                  router.push(crossLinkHref);
                }}
                text={crossLinkText}
                type="button"
                variant="transparent"
              />
            )}
          </div>
        </Form>
      </Formik>
    </div>
  );
}
